import Swal from 'sweetalert2'
import {
  buildRewardDetailModalHtml,
  type RewardDetailModalInput,
} from '@huay/shared/points/rewardDetailModalContent'
import '@huay/shared/points/rewardDetailModal.css'
import {
  buildPointsLedgerText,
  escapeRewardHtml,
  formatRewardPoints,
  type RewardRedeemFlowHandlers,
  type RewardRedeemItem,
  type RewardShippingInfo,
} from '@huay/shared/points/rewardRedeemFlow'

export const MODERN_POINTS_SWAL_CLASS = {
  popup: 'points-swal-popup',
  title: 'points-swal-title',
  htmlContainer: 'points-swal-html',
  actions: 'points-swal-actions',
  confirmButton: 'points-swal-confirm',
  cancelButton: 'points-swal-cancel',
  validationMessage: 'points-swal-validation',
} as const

const SHIPPING_FORM_HTML = `
  <div class="points-swal-body">
    <div class="points-swal-icon-wrap" aria-hidden="true">
      <i class="fas fa-truck"></i>
    </div>
    <p class="points-swal-lead">กรุณากรอกข้อมูลสำหรับจัดส่งของรางวัล</p>
    <div class="points-swal-form">
      <label class="points-swal-field" for="reward-contact-name">
        <span class="points-swal-label">ชื่อผู้รับ</span>
        <input id="reward-contact-name" class="points-swal-input" placeholder="ระบุชื่อ-นามสกุล" autocomplete="name">
      </label>
      <label class="points-swal-field" for="reward-contact-phone">
        <span class="points-swal-label">เบอร์ติดต่อ</span>
        <input id="reward-contact-phone" class="points-swal-input" placeholder="เบอร์โทรที่ติดต่อได้" inputmode="tel" autocomplete="tel">
      </label>
      <label class="points-swal-field" for="reward-contact-address">
        <span class="points-swal-label">ที่อยู่ / หมายเหตุ</span>
        <textarea id="reward-contact-address" class="points-swal-textarea" placeholder="ที่อยู่จัดส่ง หรือจุดรับรางวัล" rows="3"></textarea>
      </label>
    </div>
  </div>
`

function buildLedgerHtml(currentPoints: number, requiredPoints: number): string {
  const ledger = buildPointsLedgerText(currentPoints, requiredPoints)
  return `
    <div class="points-swal-ledger">
      <span class="points-swal-ledger__item">
        <small>คะแนนปัจจุบัน</small>
        <strong>${ledger.currentLabel}</strong>
      </span>
      <span class="points-swal-ledger__arrow" aria-hidden="true">→</span>
      <span class="points-swal-ledger__item">
        <small>ใช้แลก</small>
        <strong>${ledger.costLabel}</strong>
      </span>
      <span class="points-swal-ledger__arrow" aria-hidden="true">→</span>
      <span class="points-swal-ledger__item">
        <small>คงเหลือ</small>
        <strong>${ledger.remainingLabel}</strong>
      </span>
    </div>
  `
}

function buildShippingSummaryHtml(shippingInfo: RewardShippingInfo | null): string {
  if (!shippingInfo) return ''
  return `
    <div class="points-swal-shipping">
      <p class="points-swal-shipping__title">ข้อมูลจัดส่ง</p>
      <p><span>ชื่อผู้รับ</span> <strong>${escapeRewardHtml(shippingInfo.name)}</strong></p>
      <p><span>เบอร์ติดต่อ</span> <strong>${escapeRewardHtml(shippingInfo.phone)}</strong></p>
      ${
        shippingInfo.address
          ? `<p><span>ที่อยู่</span> <strong>${escapeRewardHtml(shippingInfo.address)}</strong></p>`
          : ''
      }
    </div>
  `
}

export function createModernRewardRedeemHandlers(): RewardRedeemFlowHandlers {
  return {
    onUnavailable: async (label) => {
      await Swal.fire({
        title: label || 'ไม่สามารถแลกรางวัลได้',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-box-open"></i>
            </div>
            <p class="points-swal-message">${escapeRewardHtml(label || 'รางวัลนี้ไม่พร้อมให้แลกในขณะนี้')}</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
    },
    onInsufficientPoints: async ({ currentPoints, requiredPoints }) => {
      await Swal.fire({
        title: 'คะแนนไม่เพียงพอ',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-coins"></i>
            </div>
            <p class="points-swal-message">คุณมี <strong>${formatRewardPoints(currentPoints)}</strong> คะแนน</p>
            <p class="points-swal-message">ต้องการ <strong>${formatRewardPoints(requiredPoints)}</strong> คะแนน</p>
            ${buildLedgerHtml(currentPoints, requiredPoints)}
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
    },
    onConfirmRedeem: async ({ reward, currentPoints }) => {
      const result = await Swal.fire({
        title: 'ยืนยันการแลกรางวัล',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--confirm" aria-hidden="true">
              <i class="fas fa-gift"></i>
            </div>
            <p class="points-swal-message">
              ใช้ <strong>${formatRewardPoints(reward.pointsCost)}</strong> คะแนน
              เพื่อแลก <strong>${escapeRewardHtml(reward.name)}</strong>
            </p>
            ${
              reward.type === 'credit'
                ? `<p class="points-swal-message">ได้เครดิต <strong>${formatRewardPoints(reward.creditAmount || 0)}</strong> บาท</p>`
                : ''
            }
            ${buildLedgerHtml(currentPoints, reward.pointsCost)}
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'ดำเนินการต่อ',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
      return result.isConfirmed
    },
    onShippingForm: async () => {
      const result = await Swal.fire({
        title: 'ข้อมูลสำหรับจัดส่ง/รับรางวัล',
        html: SHIPPING_FORM_HTML,
        showCancelButton: true,
        confirmButtonText: 'ดำเนินการต่อ',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
        focusConfirm: false,
        didOpen: () => {
          document.getElementById('reward-contact-name')?.focus()
        },
        preConfirm: () => {
          const name = (document.getElementById('reward-contact-name') as HTMLInputElement | null)?.value?.trim()
          const phone = (document.getElementById('reward-contact-phone') as HTMLInputElement | null)?.value?.trim()
          const address = (document.getElementById('reward-contact-address') as HTMLTextAreaElement | null)?.value?.trim()
          if (!name || !phone) {
            Swal.showValidationMessage('กรุณาระบุชื่อผู้รับและเบอร์ติดต่อ')
            return null
          }
          return { name, phone, address }
        },
      })
      if (!result.isConfirmed || !result.value) return null
      return result.value as RewardShippingInfo
    },
    onFinalSummary: async ({ reward, currentPoints, requiredPoints, shippingInfo }) => {
      const result = await Swal.fire({
        title: 'ยืนยันการแลกรางวัล',
        html: `
          <div class="points-swal-body">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--confirm" aria-hidden="true">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <p class="points-swal-lead">ตรวจสอบรายการก่อนตัดคะแนน</p>
            <p class="points-swal-message">
              รางวัล <strong>${escapeRewardHtml(reward.name)}</strong>
            </p>
            ${buildLedgerHtml(currentPoints, requiredPoints)}
            ${buildShippingSummaryHtml(shippingInfo)}
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยันแลกรางวัล',
        cancelButtonText: 'ยกเลิก',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
      return result.isConfirmed
    },
    onSuccess: async ({ message, reward }) => {
      await Swal.fire({
        title: message,
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--success" aria-hidden="true">
              <i class="fas fa-check"></i>
            </div>
            <p class="points-swal-message">${
              reward.type === 'item'
                ? 'ระบบบันทึกคำขอแลกรางวัลแล้ว กรุณารอแอดมินดำเนินการ'
                : 'คะแนนถูกหักและเครดิตถูกเพิ่มเรียบร้อยแล้ว'
            }</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
    },
    onError: async (message) => {
      await Swal.fire({
        title: 'แลกรางวัลไม่สำเร็จ',
        html: `
          <div class="points-swal-body points-swal-body--center">
            <div class="points-swal-icon-wrap points-swal-icon-wrap--error" aria-hidden="true">
              <i class="fas fa-times"></i>
            </div>
            <p class="points-swal-message">${escapeRewardHtml(message)}</p>
          </div>
        `,
        confirmButtonText: 'ตกลง',
        buttonsStyling: false,
        customClass: MODERN_POINTS_SWAL_CLASS,
      })
    },
  }
}

export async function showModernRewardDetailModal(reward: RewardDetailModalInput): Promise<void> {
  const html = buildRewardDetailModalHtml(reward, escapeRewardHtml)
  if (!html) return
  await Swal.fire({
    title: 'รายละเอียดรางวัล',
    html,
    confirmButtonText: 'ปิด',
    buttonsStyling: false,
    customClass: MODERN_POINTS_SWAL_CLASS,
  })
}
